/*
    Copyright © DokiApp, 2026
*/

import React, { useEffect, useState } from 'react'
import {
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useStoreActions, useStoreState } from '../../store/store.hooks'
import { Heading } from '../../components/Text'
import { NavigateBack } from '../../components/Nav/NavigateBack'
import { GradientButton, WhiteButton } from '../../components/Button'
import { useRelativeNavigate } from '../../hooks/useRelativeNavigate'
import { get } from '../../services/request'
import { MYMEDIO_URLS } from '../../constants'

const SURVEY_TEXTS = {
  '4ig-who-5': {
    title: 'mentalWellbeingDaysWho5Title',
    time: 'mentalWellbeingDaysWho5Time',
  },
  '4ig-bfi10': {
    title: 'mentalWellbeingDaysBfi10Title',
    time: 'mentalWellbeingDaysBfi10Time',
  },
  '4ig-tki': {
    title: 'mentalWellbeingDaysTkiTitle',
    time: 'mentalWellbeingDaysTkiTime',
  },
}

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
  bookingButton: {
    fontWeight: 'bold',
  },
  bottomButton: {
    width: '100%',
    border: '1px solid #D92B6E',
    color: '#1E2864',
    fontWeight: 'bold',
  },
  bottomButtons: {
    width: '100%',
    margin: '1.5rem 0 0',
    padding: '0 1rem 1rem',
  },
  headerTypography: {
    textTransform: 'uppercase',
  },
  page: {
    backgroundColor: '#fff',
    borderRadius: '15px',
    paddingBottom: '1rem',
  },
  pageHeader: {
    marginBottom: theme.spacing(3),
  },
  surveyCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    paddingBottom: '1rem',
    borderRadius: '12px',
    backgroundColor: '#fff',
    boxShadow: '0 10px 20px 0 rgba(100,115,175,0.2)',
  },
  surveyCardBody: {
    padding: '0 1rem',
  },
  surveyCards: {
    margin: 0,
    padding: '0 1rem',
    justifyContent: 'center',
  },
  surveyDescription: {
    fontSize: '0.9rem',
    textAlign: 'justify',
    margin: '0.75rem 0 0.5rem',
  },
  surveyName: {
    color: '#1E2864',
    fontWeight: 'bold',
    fontSize: '1.1rem',
    textAlign: 'center',
    border: '2px solid #1E2864',
    borderRadius: '12px',
    padding: '0.5rem 1rem',
  },
  surveyTime: {
    fontSize: '0.9rem',
    fontStyle: 'italic',
    marginBottom: '1rem',
  },
  startButton: {
    width: '90%',
    margin: '0 auto',
  },
}))

const MentalWellbeingDaysSurveys = () => {
  const classes = useStyles()
  const {
    t,
    i18n: { language },
  } = useTranslation('survey')
  const { relativeNavigate } = useRelativeNavigate()
  const { fetchSettings } = useStoreActions((actions) => actions.settings)
  const { settingsByKey } = useStoreState((state) => state.settings)

  const [surveys, setSurveys] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        setLoading(true)
        const result = await get(
          `/surveys/4ig-mental-wellbeing-days?lng=${language}`
        )
        setSurveys(result.surveys)
      } catch (err) {
        console.error('Failed to fetch surveys', err)
      } finally {
        setLoading(false)
      }
    }

    fetchSurveys()
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const navigateInNewTab = (url) => {
    if (!url) return

    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const handleStartSurvey = (wellKnownKey) => {
    relativeNavigate(`/surveys/${wellKnownKey}/questions`)
  }

  const handleBack = () => {
    relativeNavigate('/services')
  }

  const handleNavigateToEvaluation = () => {
    relativeNavigate('/surveys/4ig/evaluation')
  }

  const handleBookingClick = () => {
    navigateInNewTab(settingsByKey[MYMEDIO_URLS.MENTAL_WELLBEING_DAYS_BOOKING])
  }

  if (loading) {
    return (
      <Backdrop className={classes.backdrop} open={true}>
        <CircularProgress color="inherit" />
      </Backdrop>
    )
  }

  return (
    <Grid container className={classes.page}>
      <NavigateBack onBack={handleBack} />
      <Grid item xs={12} className={classes.pageHeader}>
        <Heading extraClasses={[classes.headerTypography]}>
          {t('mentalWellbeingDaysHeader')}
        </Heading>
      </Grid>

      <Grid item container xs={12} className={classes.surveyCards} spacing={3}>
        {surveys.map((survey) => {
          const texts = SURVEY_TEXTS[survey.wellKnownKey] || {}
          return (
            <Grid item md={4} xs={12} key={survey.wellKnownKey}>
              <Box className={classes.surveyCard}>
                <Box>
                  <Typography className={classes.surveyName}>
                    {texts.title ? t(texts.title) : survey.title}
                  </Typography>
                  <Box className={classes.surveyCardBody}>
                    <Typography className={classes.surveyDescription}>
                      {survey.intro || survey.description}
                    </Typography>
                    {texts.time && (
                      <Typography className={classes.surveyTime}>
                        {t(texts.time)}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <GradientButton
                  className={classes.startButton}
                  disabled={survey.completed}
                  onClick={() => handleStartSurvey(survey.wellKnownKey)}
                >
                  {survey.completed
                    ? t('mentalWellbeingDaysFilledButton')
                    : t('mentalWellbeingDaysFillButton')}
                </GradientButton>
              </Box>
            </Grid>
          )
        })}
      </Grid>

      <Grid container spacing={2} className={classes.bottomButtons}>
        <Grid item xs={12} md={6}>
          <WhiteButton
            className={classes.bottomButton}
            onClick={handleNavigateToEvaluation}
          >
            {t('mentalWellbeingDaysResultsButton')}
          </WhiteButton>
        </Grid>
        <Grid item xs={12} md={6}>
          <GradientButton
            className={classes.bookingButton}
            onClick={handleBookingClick}
          >
            {t('mentalWellbeingDaysBookingButton')}
          </GradientButton>
        </Grid>
      </Grid>
    </Grid>
  )
}

MentalWellbeingDaysSurveys.displayName = 'MentalWellbeingDaysSurveys'

export default MentalWellbeingDaysSurveys
export { MentalWellbeingDaysSurveys }
